import { URL_REGEX } from 'utils/regExp';

type ClassMap = Record<string, string[]>;

interface ParseTextOptions {
  markdown?: boolean;
}

const UNORDERED_LIST_REGEX = /^\s*[-*]\s+(.+)$/;
const ORDERED_LIST_REGEX = /^\s*\d+\.\s+(.+)$/;

const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const formatMarkdownInline = (value: string) =>
  escapeHtml(value)
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    )
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[\s(])\*([^*\n]+)\*(?=[$\s).,!?:;]|$)/g, '$1<em>$2</em>');

const formatMarkdown = (message: string) => {
  const blocks: string[] = [];
  const listItems: string[] = [];
  let listType: 'ol' | 'ul' | null = null;
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) return;

    blocks.push(
      `<p>${formatMarkdownInline(paragraphLines.join('\n')).replace(/\n/g, '<br />')}</p>`
    );
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listType || !listItems.length) return;

    blocks.push(
      `<${listType}>${listItems.map((item) => `<li>${formatMarkdownInline(item)}</li>`).join('')}</${listType}>`
    );
    listItems.length = 0;
    listType = null;
  };

  message
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        flushParagraph();
        flushList();
        return;
      }

      const unorderedMatch = trimmedLine.match(UNORDERED_LIST_REGEX);
      if (unorderedMatch) {
        flushParagraph();
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(unorderedMatch[1]);
        return;
      }

      const orderedMatch = trimmedLine.match(ORDERED_LIST_REGEX);
      if (orderedMatch) {
        flushParagraph();
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(orderedMatch[1]);
        return;
      }

      flushList();
      paragraphLines.push(trimmedLine);
    });

  flushParagraph();
  flushList();

  return blocks.join('');
};

const parseNode = (node: ChildNode) => {
  if (node.nodeType === Node.TEXT_NODE) {
    if (node.parentElement?.nodeName === 'A') {
      return;
    }

    const content = node.textContent ?? '';
    const replacedContent = content.replace(
      URL_REGEX,
      '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-mBase text-primary md:text-base">$1</a>'
    );
    const tempNode = document.createElement('span');
    tempNode.innerHTML = replacedContent;
    while (tempNode.firstChild) {
      node.parentNode?.insertBefore(tempNode.firstChild, node);
    }
    if (node.parentNode?.contains(node)) {
      node.parentNode.removeChild(node);
    }
  } else {
    const classMap: ClassMap = {
      CODE: ['whitespace-pre-wrap'],
      OL: ['list-decimal', 'whitespace-normal', 'pl-5'],
      UL: ['list-disc', 'whitespace-normal', 'pl-5']
    };

    if (node instanceof HTMLElement && classMap[node.nodeName]) {
      node.classList.add(...classMap[node.nodeName]);
    }
    node.childNodes.forEach(parseNode);
  }
};

const addClassesToLinks = (node: ChildNode) => {
  if (node instanceof HTMLAnchorElement) {
    node.classList.add('text-mBase', 'text-primary', 'md:text-base');
    if (node.target === '_blank') {
      node.rel = 'noopener noreferrer';
    }
  } else if (node.childNodes.length > 0) {
    node.childNodes.forEach(addClassesToLinks);
  }
};

export const parseText = (message = '', { markdown = false }: ParseTextOptions = {}) => {
  const parser = new DOMParser();
  const emailRegex = /<([^<>@\s]+@[^<>@\s]+\.[^<>@\s]+)>/g;
  const content = markdown ? formatMarkdown(message) : message;

  const doc = parser.parseFromString(
    content
      .replace(emailRegex, (match) => match.replace(/</g, '&lt;'))
      .replace(/&lt;/g, '&amp;lt;')
      .replace(/\\n|\n/g, '<br />'),
    'text/html'
  );
  doc.body.childNodes.forEach(parseNode);
  doc.body.childNodes.forEach(addClassesToLinks);

  return doc.body.innerHTML;
};
